import { storedName } from "./storedFile";
import type { FolderListing, RecordFile, ResourceKind } from "./types";

/** One item of the Files tab's DHX dataset (View 1208, src/components/Vault.tsx).
 *  `attachmentId` set = a registered file (a resource_files row). Unset = bytes in the
 *  record's folder that no row claims, shown so nothing listed before goes missing. */
export interface VaultRow {
  /** `a${file.id}` for a registered row; the key for unregistered bytes. */
  id: string;
  /** What DHX shows. HTML-ESCAPED: DHX Vault renders item.name as innerHTML. Includes any
   *  " (file missing)" suffix. */
  name: string;
  /** The RAW name, no suffix: for the download filename and snackbar text. */
  fileName: string;
  kind: ResourceKind;
  size?: number;
  /** aeronet and unregistered: where the bytes are, under the tenant root. */
  key?: string;
  /** url kind: what opens (and, from phase 3, sharepoint / gdrive). */
  url?: string;
  attachmentId?: number;
  /** aeronet rows whose bytes are not in storage (P10). Omitted when the bytes are there. */
  missing?: boolean;
  /** No size is recorded for this file. DHX's data init turns an unset size into 0 ("0 B"), so the
   *  list reads this flag to show a dash instead (Vault withListName). Omitted when the size is known. */
  sizeUnknown?: boolean;
}

/** The row's size, and the flag when there is none (a dash in the list, never "0 B"). */
function sized(size: number | null | undefined): Pick<VaultRow, "size" | "sizeUnknown"> {
  return typeof size === "number" && Number.isFinite(size) ? { size } : { sizeUnknown: true };
}

/** Appended to a missing row's shown name (and its Files tab tooltip). */
export const MISSING_SUFFIX = " (file missing)";

// DHX Vault renders item.name as HTML, so & < > " are escaped. `'` is deliberately left raw: it
// is inert in text and in the double-quoted class attribute DHX builds from the extension
// (vault.js ~9821), and escaping it breaks DHX's truncation, which slices the escaped string
// (vault.js ~9745) and would show "Owner&#39;s…" as broken entity text. Exported for the Files tab's
// just-uploaded row (src/components/Vault.tsx), which never passes through rowForFile.
export function escapeName(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/** A key's folder, with the trailing slash: "sors/12/a.pdf" -> "sors/12/". */
export function parentFolder(key: string): string {
  return key.slice(0, key.lastIndexOf("/") + 1);
}

/**
 * One record file to a Vault row.
 *
 * `present` is the live folder listing's usage-id -> key map (null when the listing failed).
 * Where it is available and the row's key is in this Vault's folder, it decides whether the
 * bytes are missing; the stored resource_object.status is only the fallback (P10, B(a)).
 */
export function rowForFile(
  file: RecordFile,
  path: string,
  present: Record<string, string> | null
): VaultRow {
  const object = file.resource_object;
  const kind: ResourceKind = object?.kind ?? "aeronet";
  const fileName =
    file.title ||
    object?.name ||
    file.filename ||
    (kind === "url" ? object?.url : null) ||
    "file";

  if (kind !== "aeronet") {
    return {
      id: `a${file.id}`,
      name: escapeName(fileName),
      fileName,
      kind,
      // The size the file object holds (the G2 import kept SharePoint's); unknown is a dash.
      ...sized(object?.size_bytes),
      url: object?.url ?? undefined,
      attachmentId: file.id,
    };
  }

  // No object yet (before the backfill, or a writer still on POST /attachments): the key
  // Vault has always used. With no object and no filename there is no key at all: the row is
  // still listed (so it can be removed) but is missing, and sits at the folder itself.
  const derived =
    present?.[String(file.id)] ?? (object?.locator || (file.filename ? `${path}${file.filename}` : null));
  const key = derived ?? path;
  const missing =
    derived === null
      ? true
      : present !== null && parentFolder(key) === path
        ? !(String(file.id) in present)
        : object?.status === "missing";

  const row: VaultRow = {
    id: `a${file.id}`,
    name: escapeName(missing ? fileName + MISSING_SUFFIX : fileName),
    fileName,
    kind,
    ...sized(object?.size_bytes),
    key,
    attachmentId: file.id,
  };
  if (missing) row.missing = true;
  return row;
}

/**
 * The Files tab's dataset for one Vault folder: the record's files, then bytes in the folder
 * that no row claims.
 *
 * A record's list returns the rows of ALL its folders (SOR: sors/, sors_po/, rma/), so an
 * aeronet row is kept only when its key's PARENT folder is this Vault's path. Never
 * startsWith: that also matches subfolders (stationery/<id>/customer/ under stationery/<id>/).
 * Links have no folder and show in every Vault of their record.
 */
export function vaultRows(
  files: readonly RecordFile[],
  listing: FolderListing | null,
  path: string
): VaultRow[] {
  const present = listing ? listing.present : null;
  const rows = files
    .map((file) => rowForFile(file, path, present))
    .filter((row) => row.kind !== "aeronet" || (row.key !== undefined && parentFolder(row.key) === path));

  const loose: VaultRow[] = (listing?.unregistered ?? []).map(({ key, size }) => {
    const base = storedName(key);
    return { id: key, name: escapeName(base), fileName: base, size, key, kind: "aeronet" };
  });
  return [...rows, ...loose];
}
